import React from 'react';
import {useTranslation} from 'react-i18next';

import {isSameMonth} from 'date-fns/isSameMonth';

import {useCalendarContext} from '../CalendarContext';
import {ECalendarType} from '../types';
import {getMonthHoveredStatus} from '../utils';
import * as S from './style';

type TProps = {
  year: number;
  onSelect: (value: Date) => void;
};

export const Year = React.memo<TProps>(({year, onSelect}) => {
  const {t} = useTranslation();

  const {
    startDate,
    endDate,
    minDate,
    maxDate,
    hoveredDate,
    setHoveredDate,
    leadDate,
    now,
    calendarType,
  } = useCalendarContext();

  const getSelectedStatus = React.useCallback(
    (currentMonth: number) => {
      const currentDate = new Date(year, currentMonth);

      return (
        (!!startDate && isSameMonth(currentDate, startDate)) ||
        (!!endDate && isSameMonth(currentDate, endDate))
      );
    },
    [startDate, endDate, year],
  );

  const getDisabledStatus = React.useCallback(
    (currentMonth: number) => {
      const currentDate = new Date(year, currentMonth);

      return (
        (!!minDate && currentDate < minDate && !isSameMonth(currentDate, minDate)) ||
        (!!maxDate && currentDate > maxDate && !isSameMonth(currentDate, maxDate))
      );
    },
    [minDate, maxDate, year],
  );

  return (
    <S.DateGrid data-selector="EdnaCalendar:year">
      {[...Array(12)].map((_, monthIndex) => {
        const isDisabled = getDisabledStatus(monthIndex);
        const nextDate = new Date(year, monthIndex, now.getDate());
        const hoveredStatus =
          calendarType === ECalendarType.DATE_RANGE
            ? getMonthHoveredStatus({
                currentDate: nextDate,
                startDate,
                endDate,
                hoveredDate,
                leadDate,
              })
            : null;
        const isSelected = getSelectedStatus(monthIndex);

        return (
          <S.DateWrapper
            key={monthIndex}
            disabled={isDisabled}
            onMouseEnter={() => setHoveredDate(nextDate)}
            onMouseLeave={() => setHoveredDate(null)}
            onClick={() => {
              onSelect(nextDate);
              setHoveredDate(null);
            }}
          >
            <S.Date
              disabled={isDisabled}
              isHovered={hoveredStatus?.isHovered}
              isEndHovered={hoveredStatus?.isEndHovered}
              isStartHovered={hoveredStatus?.isStartHovered}
            >
              <S.DateStatus
                isSelected={isSelected}
                disabled={isDisabled}
                isHovered={hoveredStatus?.isHovered}
              >
                {t(`EdnaCalendar:monthsShortKeysByIndex.${monthIndex}`)}
              </S.DateStatus>
            </S.Date>
          </S.DateWrapper>
        );
      })}
    </S.DateGrid>
  );
});
